import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { cn } from "../../../../lib/utils";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { ReferenceMentionItem } from "./types";
import { GetFilterDataTypeString } from "../../utils/formatters";

/** Imperative handle exposed to the tippy.js popup for keyboard handling. */
export type SuggestionListRef = {
  onKeyDown: NonNullable<
    ReturnType<NonNullable<SuggestionOptions<ReferenceMentionItem>["render"]>>["onKeyDown"]
  >;
};

export type SuggestionListProps = SuggestionProps<ReferenceMentionItem>;

/**
 * The suggestion dropdown shared by the "#" prompt mention and the "." property
 * mention. Renders the current list and supports arrow-key navigation plus
 * Enter to select. The whole item is handed to `command` so the mention
 * extension can carry its full attributes (type, prompt data type, parent).
 */
const SuggestionList = forwardRef<SuggestionListRef, SuggestionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectItem = (index: number) => {
    if (index >= props.items.length) {
      return;
    }
    props.command(props.items[index]);
  };

  const upHandler = () => {
    setSelectedIndex((prev) => (prev + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((prev) => (prev + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  if (props.items.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg bg-card text-card-foreground shadow-md">
      <div className="flex max-h-[350px] flex-col overflow-hidden overflow-y-auto py-[5px]">
        {props.items.map((item, index) => {
          const selected = index === selectedIndex;
          return (
            <div
              key={`${item.type}-${item.id}-${index}`}
              ref={(el: HTMLDivElement | null) => {
                itemRefs.current[index] = el;
              }}
              onClick={() => selectItem(index)}
              className={cn(
                "flex cursor-pointer flex-col items-start gap-[3px] px-4 py-1 text-[13px] font-normal",
                selected ? "bg-primary text-primary-foreground" : "hover:bg-accent"
              )}
            >
              <span className="text-body1">{item.label}</span>
              {item.type === "filter-answer" && item.promptDataType != null && (
                <span
                  className={cn(
                    "text-caption",
                    selected ? "text-primary-foreground" : "text-muted-foreground"
                  )}
                >
                  {GetFilterDataTypeString(item.promptDataType)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

SuggestionList.displayName = "SuggestionList";

export default SuggestionList;
